// Brand palette for Seim·Seim's zones. Mirrors the Claude Design
// "Guía de tokens" handoff: each zone has three pre-computed colours
// — the soft 8 % wash that fills the zone body, the 36 % border, and
// the saturated header label colour. Pre-computing here means the
// map renderer can paint solids (no globalAlpha gymnastics) and the
// two surfaces (map + timeline) stay visually identical.
//
// Keyed by the zone's ``name`` (case-insensitive). Zones outside this
// map fall through to whatever ``zone.color`` / ``zone.borderColor``
// the backend stored, so adding a new zone in the admin doesn't break
// the canvas.

interface BrandZoneColor {
  /** Solid background fill — brand hue mixed 8 % onto the cream
   *  background. The renderer paints this without any extra alpha. */
  fill: string;
  /** Border colour with 36 % alpha baked in. Reads as a soft outline
   *  rather than a hard line. */
  border: string;
  /** Stronger label colour used for the zone header and the section
   *  title in the timeline. Solid hex. */
  label: string;
}

const BRAND_COLORS: Record<string, BrandZoneColor> = {
  BAR: { fill: "#E9E5E5", border: "rgba(24,26,114,0.36)", label: "#181A72" },
  BARRA: { fill: "#FBEFDC", border: "rgba(255,145,0,0.36)", label: "#D97700" },
  COMEDOR: { fill: "#E7F1EF", border: "rgba(0,174,239,0.36)", label: "#0090C2" },
  PATIO: { fill: "#FAEAEE", border: "rgba(242,82,230,0.36)", label: "#CF34BC" },
  TERRAZA: { fill: "#FBF5E0", border: "rgba(255,222,50,0.36)", label: "#B07E07" },
};

/** Returns the brand palette for a zone name, or ``null`` when the
 *  zone is not one of the named brand zones. Trims whitespace and
 *  strips diacritics so ``"Comedór"`` and ``" comedor "`` both match
 *  the ``COMEDOR`` entry. */
export function brandZoneColor(zoneName: string | null | undefined): BrandZoneColor | null {
  if (!zoneName) {
    return null;
  }
  const normalized = zoneName
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();
  return BRAND_COLORS[normalized] ?? null;
}

/** Resolved ``{ fill, label }`` for any zone surface that needs the
 *  accent colour — the soft fill behind a chip / zone header, and the
 *  stronger label colour for text on top of it.
 *
 *  Single source of truth shared by the map, the Timeline zone
 *  headers, and the reservation mesa chips so all four read the same
 *  hue. For the five brand zones it returns the hardcoded palette;
 *  every other zone falls back to the operator-set ``color`` /
 *  ``borderColor``. When the brand override is later removed to make
 *  zone colours editable, this is the one function to change. */
export function zoneAccent(zone: { name: string; color: string; borderColor: string }): {
  fill: string;
  label: string;
} {
  const brand = brandZoneColor(zone.name);
  return {
    fill: brand?.fill ?? zone.color,
    label: brand?.label ?? zone.borderColor,
  };
}
